import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import TrustBar from "../../components/feature/TrustBar";
import AIAnswerBlock from "../../components/feature/AIAnswerBlock";
import ProcessSection from "../../components/feature/ProcessSection";
import WhyChooseUs from "../../components/feature/WhyChooseUs";
import ServiceAreas from "../../components/feature/ServiceAreas";
import Testimonials from "../../components/feature/Testimonials";
import FAQSection from "../../components/feature/FAQSection";
import FinalCTA from "../../components/feature/FinalCTA";
import HeroSection from "../home/components/HeroSection";
import ServicesOverview from "../home/components/ServicesOverview";

const CITY = "Colorado Springs";
const STATE = "CO";

const locationFAQs = [
  {
    question: `Who offers construction services in ${CITY}, ${STATE}?`,
    answer: `Westec Builders provides reliable construction and remodeling services tailored to homeowners and businesses in ${CITY} and surrounding areas including Monument, Falcon, Pueblo, Castle Rock, and Fountain — delivering quality craftsmanship, transparent pricing, and on-time project completion throughout the Pikes Peak region.`,
  },
  {
    question: `Do you work locally in ${CITY}?`,
    answer: `Yes, Westec Builders regularly completes projects throughout ${CITY}, ensuring deep local expertise, fast response times, and a thorough understanding of Colorado building codes, permit processes, climate considerations, and soil conditions specific to the Pikes Peak region.`,
  },
  {
    question: `How do I get a construction quote in ${CITY}?`,
    answer: `You can request a free estimate by calling (719) 555-0100 or submitting the online form. The Westec Builders team will contact you within 24 hours to schedule a consultation, review your project goals, and provide a detailed no-obligation estimate for your ${CITY} construction project.`,
  },
  {
    question: `What construction services are available in ${CITY}?`,
    answer: `Westec Builders offers a full range of construction services in ${CITY} including custom home building, full-scale remodeling, room and garage additions, commercial construction and build-outs, and interior and exterior upgrades — all delivered with local expertise and premium craftsmanship.`,
  },
  {
    question: `Is Westec Builders familiar with ${CITY} building codes and permits?`,
    answer: `Absolutely. As a local ${CITY} contractor, Westec Builders is fully versed in Colorado Springs building codes, Pikes Peak Regional Building Department permit requirements, zoning regulations, and local inspection processes — ensuring every project achieves full compliance efficiently and without delays.`,
  },
  {
    question: `How long does a construction project take in ${CITY}?`,
    answer: `Timelines vary by project scope. Remodeling projects in ${CITY} typically take two to eight weeks. Custom home builds range from four to twelve months depending on design complexity. Westec Builders provides a detailed project schedule during the planning phase so ${CITY} clients always know what to expect.`,
  },
];

export default function LocationPage() {
  return (
    <main>
      <Navbar />

      {/* 1 — Hero */}
      <HeroSection
        variant="half"
        bgImage="https://public.readdy.ai/ai/img_res/e39008bd489e19c331237ed4feb7bb1d.jpg"
        bgImageAlt={`Construction and remodeling services in ${CITY}, ${STATE} — Westec Builders`}
        badge={`Local Construction Experts — ${CITY}, ${STATE} • Licensed & Insured`}
        h1={
          <>
            Construction &amp; Remodeling<br />
            in <span className="text-brand-amber">{CITY}, {STATE}</span>
          </>
        }
        subtitle={`Your trusted local contractor for custom homes, renovations, additions, and commercial construction throughout ${CITY} and the Pikes Peak region.`}
        breadcrumb={[{ label: "Home", to: "/" }, { label: CITY }]}
        showDualCTA
      />

      {/* 2 — Trust Bar */}
      <TrustBar />

      {/* 3 — Services Section */}
      <ServicesOverview
        sectionLabel={`Serving the Pikes Peak Region`}
        title={<>Construction Services<br />in {CITY}</>}
        subtitle={`As ${CITY}'s trusted construction partner, Westec Builders combines local expertise with premium craftsmanship to deliver outstanding residential and commercial construction projects throughout the Pikes Peak region.`}
        showViewAll
        showPostCTA
      />

      {/* 4 — AI Answer Block */}
      <AIAnswerBlock
        sectionLabel="Local Knowledge"
        mainQuestion={`Construction Services in ${CITY}`}
        mainAnswer={`Westec Builders provides professional construction and remodeling services in ${CITY}, including custom builds, renovations, and commercial projects designed to meet local property needs. The team understands Colorado Springs' unique climate, soil conditions, building codes, and permit requirements — ensuring every project is built to perform in the local environment for decades to come.`}
        subItems={[
          {
            question: `Why Choose a Local ${CITY} Contractor?`,
            answer: `A local contractor like Westec Builders brings unmatched knowledge of ${CITY} building regulations, climate considerations, and regional supplier relationships — ensuring faster project completion, smoother permitting, and construction results built to perform in Colorado's high-altitude, variable climate environment.`,
          },
          {
            question: `How Does Westec Builders Serve the ${CITY} Community?`,
            answer: `Westec Builders is deeply invested in the ${CITY} community, building homes and commercial spaces for local residents and businesses. That personal investment translates into higher standards, stronger accountability, and consistent results that reflect the team's genuine pride in improving the local built environment.`,
          },
          {
            question: `What Local Expertise Does Westec Builders Have?`,
            answer: `With 15+ years of construction experience in ${CITY}, Westec Builders understands Pikes Peak Regional Building Department requirements, local zoning laws, altitude-specific building challenges, and Front Range climate demands — providing clients with a smoother, faster, and more compliant construction experience.`,
          },
          {
            question: `What Types of Properties Does Westec Builders Build in ${CITY}?`,
            answer: `Westec Builders constructs and renovates residential properties including custom single-family homes, townhomes, and multi-room additions, as well as commercial properties such as office suites, retail spaces, and industrial build-outs throughout ${CITY} and the surrounding Pikes Peak region.`,
          },
        ]}
      />

      {/* 5 — Process */}
      <ProcessSection subtitle={`Our proven five-step construction process ensures every ${CITY} project is completed with precision, clear communication, and consistent quality from first consultation to final walkthrough.`} />

      {/* 6 — Why Choose Us */}
      <WhyChooseUs />

      {/* 7 — Service Areas */}
      <ServiceAreas
        title={`Serving ${CITY}\nand Surrounding Areas`}
        subtitle={`Westec Builders is your trusted local construction contractor in ${CITY}, ${STATE}. We serve homeowners and businesses throughout the Pikes Peak region with professional construction and remodeling services backed by local expertise and fast response times.`}
        focusCity={CITY}
      />

      {/* 8 — Testimonials */}
      <Testimonials
        testimonials={[
          {
            quote: `We found Westec Builders through a local referral and couldn't be happier. They built our custom home in ${CITY} with incredible attention to detail and delivered on time.`,
            name: "Mark & Susan D.",
            project: `Custom Home Build — ${CITY}`,
          },
          {
            quote: "The team truly understands what building in Colorado requires. Our remodel was done perfectly with full knowledge of local codes and climate needs. Outstanding service.",
            name: "Angela P.",
            project: `Home Remodel — ${CITY}, CO`,
          },
          {
            quote: `Fast, professional, and local. Westec Builders understood our timeline and our community — they are the best contractor in the ${CITY} area, hands down.`,
            name: "Chris N.",
            project: "Home Addition — Monument, CO",
          },
          {
            quote: `As a local business owner, I needed a contractor who knew ${CITY}. Westec delivered our commercial space flawlessly — on time, on budget, above quality expectations.`,
            name: "Rachel B.",
            project: `Commercial Build-Out — ${CITY}`,
          },
          {
            quote: "Westec Builders navigated the local permit process faster than any contractor we've used before. The finished result was spectacular and the team was a pleasure to work with.",
            name: "Tom & Lisa F.",
            project: `Custom Home — Black Forest, CO`,
          },
          {
            quote: "Their knowledge of Colorado construction is unmatched. From foundation considerations to roofing for Colorado weather, every decision was made with local expertise.",
            name: "James K.",
            project: `Residential Renovation — ${CITY}`,
          },
        ]}
      />

      {/* 9 — FAQ */}
      <FAQSection
        faqs={locationFAQs}
        title={`FAQs — Construction in ${CITY}`}
      />

      {/* 10 — Final CTA */}
      <FinalCTA
        title={`Start Your ${CITY} Construction Project`}
        subtitle={`Get a free, no-obligation estimate from ${CITY}'s trusted construction and remodeling experts. We respond within 24 hours.`}
      />

      <Footer />
    </main>
  );
}
